import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Check,
    Clock,
    Facebook,
    Leaf,
    Link2,
    Loader2,
    MessageCircle,
    Plus,
    Share2,
    Snowflake,
    Twitter,
    Users,
} from "lucide-react";
import { toast } from "sonner";
import { fetchProduct, fetchProducts } from "../lib/api";
import { useCart } from "../context/CartContext";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { CartDrawer } from "../components/site/CartDrawer";
import { WhatsAppButton } from "../components/site/WhatsAppButton";

const NUTRI_LABELS = {
    energia_kcal: ["Energia", "kcal"],
    hidratos_g: ["Hidratos de carbono", "g"],
    acucares_g: ["dos quais açúcares", "g"],
    fibra_g: ["Fibra", "g"],
    proteina_g: ["Proteína", "g"],
    vitamina_c_mg: ["Vitamina C", "mg"],
};

export const formatPtDate = (iso) => {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleDateString("pt-PT", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });
    } catch {
        return iso;
    }
};

export default function FlavorDetail() {
    const { flavorId } = useParams();
    const { addItem } = useCart();
    const [product, setProduct] = useState(null);
    const [others, setOthers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    useEffect(() => {
        setLoading(true);
        setNotFound(false);
        window.scrollTo(0, 0);
        fetchProduct(flavorId)
            .then(setProduct)
            .catch(() => setNotFound(true))
            .finally(() => setLoading(false));
        fetchProducts()
            .then((all) => setOthers(all.filter((p) => p.id !== flavorId)))
            .catch(() => {});
    }, [flavorId]);

    const handleAdd = () => {
        addItem(product);
        toast.success(`${product.name} adicionado ao carrinho`);
    };

    // Social preview meta tags (client-side)
    useEffect(() => {
        if (!product) return;
        const prevTitle = document.title;
        document.title = `${product.name} — Sabor Líquido`;
        const setMeta = (attr, key, content) => {
            let el = document.head.querySelector(`meta[${attr}="${key}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute(attr, key);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };
        setMeta("property", "og:title", `${product.name} — Sabor Líquido`);
        setMeta("property", "og:description", product.description || "");
        setMeta("property", "og:image", product.image || "");
        setMeta("property", "og:url", window.location.href);
        setMeta("property", "og:type", "product");
        setMeta("property", "og:site_name", "Sabor Líquido");
        setMeta("name", "twitter:card", "summary_large_image");
        setMeta("name", "twitter:title", `${product.name} — Sabor Líquido`);
        setMeta("name", "twitter:image", product.image || "");
        let ld = document.getElementById("flavor-jsonld");
        if (!ld) {
            ld = document.createElement("script");
            ld.type = "application/ld+json";
            ld.id = "flavor-jsonld";
            document.head.appendChild(ld);
        }
        ld.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            name: product.name,
            description: product.description,
            image: product.image,
            brand: { "@type": "Brand", name: "Sabor Líquido" },
            offers: {
                "@type": "Offer",
                price: product.price.toFixed(2),
                priceCurrency: "EUR",
                availability: "https://schema.org/InStock",
                url: window.location.href,
            },
        });
        return () => {
            document.title = prevTitle;
            ld?.remove();
        };
    }, [product]);

    const shareUrl = typeof window !== "undefined" ? window.location.href : "";
    const shareText = product
        ? `${product.name} — polpa 100% fruta da Sabor Líquido`
        : "";

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            toast.success("Link copiado!");
        } catch {
            toast.error("Não foi possível copiar o link");
        }
    };

    if (loading) {
        return (
            <div className="bg-brand-cream min-h-screen" data-testid="flavor-loading">
                <Header />
                <div className="flex justify-center py-40">
                    <Loader2 className="h-8 w-8 animate-spin text-brand-orange" />
                </div>
            </div>
        );
    }

    if (notFound || !product) {
        return (
            <div className="bg-brand-cream min-h-screen" data-testid="flavor-not-found">
                <Header />
                <div className="max-w-3xl mx-auto px-4 py-32 text-center">
                    <h1 className="font-display font-medium text-4xl text-brand-ink">
                        Sabor não encontrado.
                    </h1>
                    <Link
                        to="/#produtos"
                        data-testid="flavor-back-home"
                        className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-brand-orange hover:underline"
                    >
                        <ArrowLeft className="h-4 w-4" /> Voltar aos sabores
                    </Link>
                </div>
                <Footer />
            </div>
        );
    }

    const nutrition = product.nutrition || {};
    const uses = product.uses || [];
    const recipes = product.recipes || [];
    const harvest = formatPtDate(product.harvest_date);
    const freeze = formatPtDate(product.freeze_date);
    const sameDay =
        product.harvest_date && product.harvest_date === product.freeze_date;

    return (
        <div className="bg-brand-cream min-h-screen" data-testid="flavor-detail-page">
            <Header />

            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-24">
                <nav
                    className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-brand-ink-soft"
                    data-testid="flavor-breadcrumb"
                >
                    <Link to="/#produtos" className="hover:text-brand-orange transition-colors">
                        Sabores
                    </Link>
                    <span>/</span>
                    <span className="text-brand-ink font-semibold">{product.name}</span>
                </nav>

                <div className="mt-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
                    {/* Big photo + freshness seal overlay */}
                    <div className="relative">
                        <div
                            className="relative rounded-2xl overflow-hidden shadow-pop"
                            style={{ background: (product.color || "#EBE8E1") + "33" }}
                        >
                            <img
                                src={product.image}
                                alt={product.name}
                                data-testid="flavor-image"
                                className="w-full h-[420px] md:h-[560px] object-cover"
                            />
                        </div>
                        {harvest && (
                            <div
                                className="absolute -bottom-6 left-6 right-6 sm:right-auto bg-white rounded-2xl px-5 py-4 shadow-pop border border-brand-ink/5 flex items-center gap-4"
                                data-testid="flavor-freshness-seal"
                            >
                                <div className="h-12 w-12 shrink-0 rounded-full bg-brand-green-soft flex items-center justify-center">
                                    <Snowflake className="h-5 w-5 text-brand-green" />
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-orange">
                                        Selo de Frescura
                                    </div>
                                    <div className="text-sm font-semibold text-brand-ink mt-0.5">
                                        Colhido a {harvest}
                                    </div>
                                    <div className="text-xs text-brand-ink-soft">
                                        {sameDay
                                            ? "Congelado a -18 °C no próprio dia"
                                            : `Congelado a -18 °C a ${freeze}`}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Info column */}
                    <div className="lg:pt-4">
                        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">
                            <Leaf className="h-3.5 w-3.5" /> Polpa 100% fruta
                            {product.badge && (
                                <span
                                    className="rounded-full bg-brand-orange text-white px-2.5 py-1 text-[10px] tracking-wider"
                                    data-testid="flavor-badge"
                                >
                                    {product.badge}
                                </span>
                            )}
                        </span>
                        <h1
                            className="mt-3 font-display font-medium text-5xl md:text-6xl text-brand-ink tracking-tight leading-none"
                            data-testid="flavor-name"
                        >
                            {product.name}
                        </h1>
                        <p
                            className="mt-5 text-lg text-brand-ink-soft leading-relaxed max-w-xl"
                            data-testid="flavor-description"
                        >
                            {product.description}
                        </p>

                        <div className="mt-8 flex items-center gap-5">
                            <div
                                className="font-display font-medium text-4xl text-brand-ink"
                                data-testid="flavor-price"
                            >
                                {product.price.toFixed(2).replace(".", ",")} €
                            </div>
                            <button
                                type="button"
                                onClick={handleAdd}
                                data-testid="flavor-add-to-cart"
                                className="inline-flex items-center gap-2 rounded-full bg-brand-ink hover:bg-[#2C312E] text-brand-cream px-7 py-4 text-sm font-semibold transition-all shadow-pop hover:translate-y-[-2px]"
                            >
                                <Plus className="h-4 w-4" /> Adicionar ao carrinho
                            </button>
                        </div>

                        {/* Share bar */}
                        <div
                            className="mt-6 flex items-center gap-2 flex-wrap"
                            data-testid="flavor-share"
                        >
                            <span className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-brand-ink-soft mr-1">
                                <Share2 className="h-3.5 w-3.5" /> Partilhar
                            </span>
                            <a
                                href={`https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`}
                                target="_blank"
                                rel="noreferrer"
                                aria-label="Partilhar no WhatsApp"
                                data-testid="share-whatsapp"
                                className="h-9 w-9 rounded-full bg-white border border-brand-ink/10 hover:bg-brand-green hover:text-white text-brand-ink flex items-center justify-center transition-colors"
                            >
                                <MessageCircle className="h-4 w-4" />
                            </a>
                            <a
                                href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                                target="_blank"
                                rel="noreferrer"
                                aria-label="Partilhar no Facebook"
                                data-testid="share-facebook"
                                className="h-9 w-9 rounded-full bg-white border border-brand-ink/10 hover:bg-brand-ink hover:text-white text-brand-ink flex items-center justify-center transition-colors"
                            >
                                <Facebook className="h-4 w-4" />
                            </a>
                            <a
                                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`}
                                target="_blank"
                                rel="noreferrer"
                                aria-label="Partilhar no X"
                                data-testid="share-x"
                                className="h-9 w-9 rounded-full bg-white border border-brand-ink/10 hover:bg-brand-ink hover:text-white text-brand-ink flex items-center justify-center transition-colors"
                            >
                                <Twitter className="h-4 w-4" />
                            </a>
                            <button
                                type="button"
                                onClick={copyLink}
                                aria-label="Copiar link"
                                data-testid="share-copy-link"
                                className="inline-flex items-center gap-1.5 h-9 rounded-full bg-white border border-brand-ink/10 hover:bg-brand-orange hover:text-white text-brand-ink px-3.5 text-xs font-semibold transition-colors"
                            >
                                <Link2 className="h-3.5 w-3.5" /> Copiar link
                            </button>
                        </div>

                        {/* Nutrition */}
                        {Object.keys(nutrition).length > 0 && (
                            <div className="mt-12" data-testid="flavor-nutrition">
                                <h2 className="font-display font-medium text-2xl text-brand-ink">
                                    Perfil nutricional{" "}
                                    <span className="text-brand-ink-soft text-lg italic">
                                        — por 100 ml
                                    </span>
                                </h2>
                                <dl className="mt-4 border-t border-brand-ink/10">
                                    {Object.entries(NUTRI_LABELS).map(([key, [label, unit]]) =>
                                        nutrition[key] === undefined ? null : (
                                            <div
                                                key={key}
                                                data-testid={`nutrition-${key}`}
                                                className="flex items-baseline justify-between py-3 border-b border-brand-ink/10"
                                            >
                                                <dt className="text-sm text-brand-ink-soft">
                                                    {label}
                                                </dt>
                                                <dd className="font-display font-medium text-xl text-brand-ink">
                                                    {nutrition[key]}
                                                    <span className="text-xs font-sans text-brand-ink-soft ml-1">
                                                        {unit}
                                                    </span>
                                                </dd>
                                            </div>
                                        ),
                                    )}
                                </dl>
                            </div>
                        )}

                        {/* Mini recipes (fallback: usage suggestions) */}
                        {recipes.length > 0 ? (
                            <div className="mt-10" data-testid="flavor-recipes">
                                <h2 className="font-display font-medium text-2xl text-brand-ink">
                                    Mini-receita{recipes.length > 1 ? "s" : ""}{" "}
                                    <span className="italic text-brand-orange">da casa.</span>
                                </h2>
                                {recipes.map((r, i) => (
                                    <article
                                        key={i}
                                        data-testid={`flavor-recipe-${i}`}
                                        className="mt-5 rounded-2xl bg-white shadow-soft overflow-hidden md:flex"
                                    >
                                        {r.photo && (
                                            <div className="md:w-2/5 overflow-hidden">
                                                <img
                                                    src={r.photo}
                                                    alt={r.title}
                                                    data-testid={`recipe-photo-${i}`}
                                                    className="w-full h-52 md:h-full object-cover hover:scale-105 transition-transform duration-700"
                                                    loading="lazy"
                                                />
                                            </div>
                                        )}
                                        <div className="flex-1 p-6 md:p-7">
                                            <h3
                                                className="font-display font-medium text-2xl text-brand-ink"
                                                data-testid={`recipe-title-${i}`}
                                            >
                                                {r.title}
                                            </h3>
                                            <div className="mt-2 flex items-center gap-3 text-xs text-brand-ink-soft">
                                                {r.time && (
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Clock className="h-3.5 w-3.5 text-brand-orange" />
                                                        {r.time}
                                                    </span>
                                                )}
                                                {r.servings && (
                                                    <span className="inline-flex items-center gap-1.5">
                                                        <Users className="h-3.5 w-3.5 text-brand-orange" />
                                                        {r.servings}
                                                    </span>
                                                )}
                                            </div>
                                            <ol className="mt-4 space-y-2.5">
                                                {(r.steps || []).map((s, j) => (
                                                    <li
                                                        key={j}
                                                        data-testid={`recipe-step-${i}-${j}`}
                                                        className="flex items-start gap-3"
                                                    >
                                                        <span className="shrink-0 h-6 w-6 rounded-full bg-brand-cream font-display font-medium text-sm text-brand-orange flex items-center justify-center">
                                                            {j + 1}
                                                        </span>
                                                        <span className="text-sm text-brand-ink-soft leading-relaxed">
                                                            {s}
                                                        </span>
                                                    </li>
                                                ))}
                                            </ol>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        ) : (
                            uses.length > 0 && (
                                <div className="mt-10" data-testid="flavor-uses">
                                    <h2 className="font-display font-medium text-2xl text-brand-ink">
                                        Sugestões de uso
                                    </h2>
                                    <ul className="mt-4 space-y-3">
                                        {uses.map((u, i) => (
                                            <li
                                                key={i}
                                                data-testid={`flavor-use-${i}`}
                                                className="flex items-start gap-3"
                                            >
                                                <span className="mt-0.5 shrink-0 h-5 w-5 rounded-full bg-brand-orange-soft flex items-center justify-center">
                                                    <Check className="h-3 w-3 text-brand-orange" />
                                                </span>
                                                <span className="text-brand-ink-soft leading-relaxed">
                                                    {u}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )
                        )}
                    </div>
                </div>

                {/* Other flavors */}
                {others.length > 0 && (
                    <section className="mt-24" data-testid="flavor-others">
                        <div className="flex items-end justify-between gap-6">
                            <h2 className="font-display font-medium text-3xl md:text-4xl text-brand-ink">
                                Outros sabores{" "}
                                <span className="italic text-brand-orange">da coleção.</span>
                            </h2>
                            <Link
                                to="/#produtos"
                                className="hidden sm:inline-flex items-center gap-1.5 text-sm font-bold text-brand-ink hover:text-brand-orange transition-colors"
                                data-testid="flavor-see-all"
                            >
                                Ver todos
                            </Link>
                        </div>
                        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
                            {others.map((p) => (
                                <Link
                                    key={p.id}
                                    to={`/sabores/${p.id}`}
                                    data-testid={`other-flavor-${p.id}`}
                                    className="group rounded-2xl overflow-hidden bg-white shadow-soft hover:shadow-pop transition-all hover:-translate-y-1"
                                >
                                    <div
                                        className="aspect-square overflow-hidden"
                                        style={{ background: (p.color || "#EBE8E1") + "33" }}
                                    >
                                        <img
                                            src={p.image}
                                            alt={p.name}
                                            loading="lazy"
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                        />
                                    </div>
                                    <div className="p-3">
                                        <div className="font-display font-medium text-base text-brand-ink truncate">
                                            {p.name}
                                        </div>
                                        <div className="text-xs text-brand-ink-soft mt-0.5">
                                            {p.price.toFixed(2).replace(".", ",")} €
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                <div className="mt-16">
                    <Link
                        to="/#produtos"
                        data-testid="flavor-back"
                        className="inline-flex items-center gap-2 text-sm font-bold text-brand-ink-soft hover:text-brand-ink transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" /> Voltar à coleção
                    </Link>
                </div>
            </main>

            <Footer />
            <CartDrawer />
            <WhatsAppButton />
        </div>
    );
}
